import React from 'react';
import clsx from 'clsx';
import { Trans, useTranslation } from 'react-i18next';
import { Typography } from 'ui-components';
import {
  CALC_OUTCOME_TEST_ID,
  RETIREMENT_CALCULATOR_NAMESPACE,
} from '../../tools/RetirementCalculator.const';
import { useFormatAmount } from '../../tools/useFormatAmount';
import { RetirementCalculatorOutcomeResultPropsModel } from './RetirementCalculatorOutcome.model';

import useStyles from './RetirementCalculatorOutcome.style';

export const RetirementCalculatorOutcomeResult: React.FC<
  RetirementCalculatorOutcomeResultPropsModel
> = ({ promotedProduct, productName, withSavings, promotedPayout, total, payoutYears }) => {
  const c = useStyles({});
  const [t] = useTranslation(RETIREMENT_CALCULATOR_NAMESPACE, { useSuspense: false });
  const amount = useFormatAmount();
  const taxNote = t(`outcome.result.tax-note.${promotedProduct}`, { defaultValue: '' });

  return (
    <>
      <div className={c.resultRow}>
        <Typography fontWeight="bold">
          {t('outcome.result.with-savings')}
        </Typography>
        <Typography variant="h4" component="p" fontWeight="bold" className={c.resultValue}>
          {amount(withSavings)}
        </Typography>
      </div>

      <div className={c.resultRow}>
        <div className={c.resultLabelGroup}>
          <Typography fontWeight="bold">
            {t('outcome.result.product-payout', { product: productName })}
          </Typography>
          <Typography variant="body2" component="p">
            {t('outcome.result.payout-years', { count: payoutYears })}
          </Typography>
        </div>
        <Typography variant="h4" component="p" fontWeight="bold" className={c.resultValue}>
          {amount(promotedPayout)}
        </Typography>
      </div>

      <div className={c.resultRow}>
        <div className={c.resultLabelGroup}>
          <Typography variant="h3" component="p" fontWeight="bold" className={c.accentText}>
            {t('outcome.result.total', { product: productName })}
          </Typography>
          <Typography variant="body2" component="p">
            {t('outcome.result.total-description', { product: productName, count: payoutYears })}
          </Typography>
        </div>
        <Typography
          variant="h1"
          component="p"
          fontWeight="extraBold"
          className={clsx(c.totalValue, c.accentText)}
          testId={`${CALC_OUTCOME_TEST_ID}-total`}
        >
          {amount(total)}
        </Typography>
      </div>

      {!!taxNote && (
        <div className={c.taxNote} data-testid={`${CALC_OUTCOME_TEST_ID}-tax-note`}>
          <Typography>
            <Trans
              t={t}
              i18nKey={`outcome.result.tax-note.${promotedProduct}`}
              components={{ accent: <strong className={c.taxNoteAccent} /> }}
            />
          </Typography>
        </div>
      )}
    </>
  );
};
